import React, { type FC } from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

import { SwiperElement } from '@/components/molecules/SwiperElement';
import type { ItemsContainerProps } from '@/components/layout/ItemsContainer/ItemsContainer.types';

export const gridContainerVariants = cva('grid-cols-1 md:grid-cols-2', {
    variants: {
        breakpoint: {
            sm: 'hidden sm:grid',
            md: 'hidden md:grid',
            lg: 'hidden lg:grid',
            none: 'grid',
        },
        columns: {
            2: 'lg:grid-cols-2',
            3: 'lg:grid-cols-3',
            4: 'lg:grid-cols-4',
            5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5',
            custom: '',
        },
        gap: {
            none: 'gap-0',
            s: 'gap-2',
            m: 'gap-4',
            l: 'gap-6',
            xl: 'gap-8',
            custom: '',
        },
    },
    defaultVariants: {
        columns: 3,
        gap: 'm',
        breakpoint: 'none',
    },
});

export const flexContainerVariants = cva('md:flex', {
    variants: {
        preset: {
            hero: 'flex-col items-center justify-between gap-12 md:flex-row md:gap-5 lg:gap-16',
            heroReverse:
                'flex-col items-center justify-between gap-12 md:flex-row-reverse md:gap-5 lg:gap-16',
            topAlign:
                'flex-col justify-between gap-12 md:flex-row md:gap-5 lg:gap-16',

            full: '!block md:block',
        },
        breakpoint: {
            sm: 'hidden sm:flex',
            md: 'hidden md:flex',
            lg: 'hidden lg:flex',
            none: 'flex',
        },
    },
    defaultVariants: {
        breakpoint: 'none',
    },
});

export const swiperContainerVariants = cva('', {
    variants: {
        breakpoint: {
            sm: 'block sm:!hidden',
            md: 'block md:!hidden',
            lg: 'block lg:!hidden',
            none: '!hidden',
        },
    },
});

const ItemsContainer: FC<ItemsContainerProps> = ({
    gridOptions,
    flexOptions = {},
    swiperOptions,
    children,
    className,
    asList,
    ...props
}) => {
    const Tag = asList ? 'ul' : 'div';

    return (
        <>
            <Tag
                className={cn(
                    !!gridOptions
                        ? gridContainerVariants({
                              ...gridOptions,
                              breakpoint: swiperOptions?.breakpoint || 'none',
                          })
                        : flexContainerVariants({
                              ...flexOptions,
                              breakpoint: swiperOptions?.breakpoint || 'none',
                          }),
                    !!swiperOptions && 'hidden',
                    className,
                )}
                {...props}
            >
                {children}
            </Tag>

            {!!swiperOptions && (
                <SwiperElement
                    {...swiperOptions.swiperProps}
                    className={swiperContainerVariants({
                        breakpoint: swiperOptions.breakpoint || 'none',
                        className: cn(
                            className,
                            swiperOptions.swiperProps.className,
                        ),
                    })}
                >
                    {children}
                </SwiperElement>
            )}
        </>
    );
};

export default ItemsContainer;
